export const TRANSCRIPTION_PERMISSION_TAGS = [
	'TRANSCRIPTION',
	'TRANSCRIPTION_FOR_DEFAULT_SESSIONS',
	'TRANSCRIPTION_MOCK',
] as const satisfies readonly Professionals.PermissionTag[];

const PERMISSION_TAG_ORDER: Professionals.PermissionTag[] = [
	'DEFAULT',
	'P2P',
	'AI_SANDBOX',
	'B2C',
	'TRANSCRIPTION',
	'TRANSCRIPTION_FOR_DEFAULT_SESSIONS',
	'TRANSCRIPTION_MOCK',
];

export interface ProfessionalPermissionTagOption {
	value: Professionals.PermissionTag;
	label: string;
	hint: string;
	required?: boolean;
}

export const PROFESSIONAL_PERMISSION_TAG_OPTIONS: ProfessionalPermissionTagOption[] =
	[
		{
			value: 'DEFAULT',
			label: 'Padrão',
			hint: 'Permissão base obrigatória da plataforma.',
			required: true,
		},
		{
			value: 'P2P',
			label: 'Agendar pacientes',
			hint: 'Permite que o profissional crie, edite e agende consultas para pacientes próprios na plataforma.',
		},
		{
			value: 'AI_SANDBOX',
			label: 'Sandbox de IA (mock)',
			hint: 'O profissional não consome a IA real; notas de sessão são geradas localmente.',
		},
		{
			value: 'B2C',
			label: 'B2C',
			hint: 'Habilita funcionalidades B2C do profissional, como gestão de horários.',
		},
		{
			value: 'TRANSCRIPTION',
			label: 'Transcrição P2P',
			hint: 'Habilita transcrição e pós-processamento de IA em sessões P2P online. Exige tag P2P. Alterações atualizam sessões futuras elegíveis e enfileiram reconciliação de sala Daily.co; sessões já finalizadas não mudam.',
		},
		{
			value: 'TRANSCRIPTION_FOR_DEFAULT_SESSIONS',
			label: 'Transcrição B2C (DEFAULT)',
			hint: 'Habilita transcrição e pós-processamento de IA em sessões DEFAULT online pagas. Independente da tag TRANSCRIPTION (P2P). Alterações atualizam sessões futuras elegíveis; presencial ignora.',
		},
		{
			value: 'TRANSCRIPTION_MOCK',
			label: 'Transcrição mock (interno)',
			hint: 'Uso interno (dev/staging/QA). Simula o pipeline de transcrição sem Daily.co real. Precede TRANSCRIPTION e TRANSCRIPTION_FOR_DEFAULT_SESSIONS. Alterações atualizam sessões futuras elegíveis.',
		},
	];

export const PROFESSIONAL_PERMISSION_TAG_FILTER_OPTIONS: Array<{
	value: Exclude<Professionals.PermissionTag, 'DEFAULT'>;
	label: string;
}> = [
	{ value: 'P2P', label: 'Sessões P2P' },
	{ value: 'AI_SANDBOX', label: 'Sandbox de IA' },
	{ value: 'B2C', label: 'B2C (front)' },
	{ value: 'TRANSCRIPTION', label: 'Transcrição P2P' },
	{
		value: 'TRANSCRIPTION_FOR_DEFAULT_SESSIONS',
		label: 'Transcrição B2C',
	},
	{ value: 'TRANSCRIPTION_MOCK', label: 'Transcrição mock' },
];

const FILTERABLE_PERMISSION_TAGS =
	PROFESSIONAL_PERMISSION_TAG_FILTER_OPTIONS.map((option) => option.value);

export const parsePermissionTagsFromSearchParams = (
	searchParams: URLSearchParams,
): Professionals.PermissionTag[] => {
	const raw = searchParams.getAll('permission_tags');
	if (raw.length === 0) return [];

	const parsed = raw
		.flatMap((value) => value.split(','))
		.map((tag) => tag.trim())
		.filter(Boolean);

	return parsed.filter((tag): tag is Professionals.PermissionTag =>
		FILTERABLE_PERMISSION_TAGS.includes(
			tag as Exclude<Professionals.PermissionTag, 'DEFAULT'>,
		),
	);
};

export const ensureDefaultPermissionTags = (
	tags: Professionals.PermissionTag[],
): Professionals.PermissionTag[] => {
	const unique = new Set<Professionals.PermissionTag>(['DEFAULT', ...tags]);
	return PERMISSION_TAG_ORDER.filter((tag) => unique.has(tag));
};

export const toggleProfessionalPermissionTag = (
	current: Professionals.PermissionTag[],
	tag: Professionals.PermissionTag,
	enabled: boolean,
): Professionals.UpdatePermissionTags => {
	if (tag === 'DEFAULT') {
		return { permission_tags: ensureDefaultPermissionTags(current) };
	}

	const without = current.filter((currentTag) => currentTag !== tag);
	const next = enabled ? [...without, tag] : without;
	return { permission_tags: ensureDefaultPermissionTags(next) };
};

export const professionalHasP2PPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean => professional?.permission_tags?.includes('P2P') ?? false;

export const professionalHasAISandboxPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean => professional?.permission_tags?.includes('AI_SANDBOX') ?? false;

export const professionalHasB2CPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean => professional?.permission_tags?.includes('B2C') ?? false;

export const professionalHasTranscriptionPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean => professional?.permission_tags?.includes('TRANSCRIPTION') ?? false;

export const professionalHasTranscriptionForDefaultSessionsPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean =>
	professional?.permission_tags?.includes(
		'TRANSCRIPTION_FOR_DEFAULT_SESSIONS',
	) ?? false;

export const professionalHasTranscriptionMockPermission = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean =>
	professional?.permission_tags?.includes('TRANSCRIPTION_MOCK') ?? false;

export const professionalHasP2PTranscriptionOnlyEligibility = (
	professional:
		| Pick<Professionals.Professional, 'permission_tags'>
		| null
		| undefined,
): boolean =>
	professionalHasP2PPermission(professional) &&
	(professionalHasTranscriptionPermission(professional) ||
		professionalHasTranscriptionMockPermission(professional));

export const getProfessionalPermissionTagLabel = (
	tag: Professionals.PermissionTag,
): string =>
	PROFESSIONAL_PERMISSION_TAG_OPTIONS.find((option) => option.value === tag)
		?.label ?? tag;

export const hasTranscriptionPermissionTagsChanged = (
	previous: Professionals.PermissionTag[],
	next: Professionals.PermissionTag[],
): boolean => {
	const previousSet = new Set(
		previous.filter((tag) =>
			(TRANSCRIPTION_PERMISSION_TAGS as readonly string[]).includes(tag),
		),
	);
	const nextSet = new Set(
		next.filter((tag) =>
			(TRANSCRIPTION_PERMISSION_TAGS as readonly string[]).includes(tag),
		),
	);

	if (previousSet.size !== nextSet.size) return true;

	for (const tag of previousSet) {
		if (!nextSet.has(tag)) return true;
	}

	return false;
};
